package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.core.uuidV7
import com.dotdrives.model.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

data class RecurringChangeConfirmation(val schedule: RecurringSchedule, val action: String, val amount: String? = null)

data class RecurringManagementUiState(
    val confirmation: RecurringChangeConfirmation? = null,
    val pendingOperation: DonorRecurringRequest? = null,
    val busy: Boolean = false,
    val error: String? = null,
    val message: String? = null,
)

/** One organization/account mount. Persist before sending, reuse the request on every retry. */
class RecurringManagementController(
    private val orgId: String,
    scope: CoroutineScope,
    private val send: suspend (DonorRecurringRequest) -> Result<DonorRecurringResult>,
    private val store: DonorOperationStore = MemoryDonorOperationStore(),
) : DotController<RecurringManagementUiState>(RecurringManagementUiState(), scope) {
    private var generation = 0
    private var job: Job? = null
    private var closed = false

    fun start() {
        if (closed) return
        try { update { it.copy(pendingOperation = savedRequest()) } }
        catch (e: Throwable) { failure(e) }
    }

    private fun savedRequest(): DonorRecurringRequest? = store.read(orgId)?.also {
        require(it.orgId == orgId && it.action in setOf("amount", "pause", "resume", "cancel")) {
            "Saved recurring request is unavailable."
        }
    }

    fun begin(schedule: RecurringSchedule, action: String, amount: String? = null) {
        if (closed || state.value.busy || state.value.pendingOperation != null) return
        update { it.copy(confirmation = null, error = null, message = null) }
        if (action !in schedule.managementActions()) return
        if (action == "amount" && (amount == null || !amount.matches(Regex("[0-9]{1,7}(\\.[0-9]{1,2})?")) ||
                amount.toDoubleOrNull()?.let { it in 0.5..1000000.0 } != true)) {
            update { it.copy(error = "Enter an amount from $0.50 to $1,000,000 with at most two decimals.") }
            return
        }
        update { it.copy(confirmation = RecurringChangeConfirmation(schedule, action, if (action == "amount") amount else null)) }
    }

    fun dismiss() { if (!state.value.busy) update { it.copy(confirmation = null) } }

    fun confirm() {
        val change = state.value.confirmation ?: return
        if (closed || state.value.busy || state.value.pendingOperation != null) return
        try {
            // A second mounted view may have saved a request since this view loaded.
            savedRequest()?.let { saved ->
                update { it.copy(confirmation = null, pendingOperation = saved) }
                return
            }
            val request = DonorRecurringRequest(change.action, orgId, change.schedule.id, uuidV7(), change.amount)
            store.save(request)
            update { it.copy(confirmation = null, pendingOperation = request) }
            retry()
        } catch (e: Throwable) { failure(e) }
    }

    fun retry() {
        val request = state.value.pendingOperation ?: return
        if (closed || state.value.busy) return
        val ticket = generation
        update { it.copy(busy = true, error = null, message = null) }
        job = scope.launch {
            try {
                val result = send(request).getOrThrow()
                check(result.accepted && result.status == "pending_confirmation" && result.url == null) {
                    "The response was incomplete. Retry the same request."
                }
                if (closed || ticket != generation) return@launch
                if (store.read(orgId)?.operationId == request.operationId) store.clear(orgId)
                update { it.copy(pendingOperation = null,
                    message = "Request accepted; awaiting Stripe confirmation. Reload to check the recorded status.") }
            } catch (e: Throwable) {
                if (e is CancellationException) throw e
                if (ticket == generation && !closed) {
                    if (e is RecurringRequestRejected) {
                        try {
                            if (store.read(orgId)?.operationId == request.operationId) store.clear(orgId)
                            update { it.copy(pendingOperation = null, error = e.message) }
                        } catch (storageError: Throwable) { failure(storageError) }
                    } else failure(e)
                }
            } finally {
                if (ticket == generation && !closed) update { it.copy(busy = false) }
            }
        }
    }

    fun close() {
        closed = true
        generation++
        job?.cancel()
        update { RecurringManagementUiState() }
    }

    private fun failure(e: Throwable) {
        if (e is CancellationException) throw e
        update { it.copy(error = e.message ?: "Unable to change this schedule. Retry the same request.") }
    }
}
